import { useEffect, useState } from "react";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

import PageSection from "./PageSection";
import SectionHeader from "../components/SectionHeader";
import ArtworkCard from "../components/cards/ArtworkCard";
import type { Artwork } from "../types/artwork";

interface FeaturedArtworksSectionProps {
  artworks: Artwork[];
}
import { useSiteTheme } from "../theme/ThemeContext";
export default function FeaturedArtworksSection({
  artworks,
}: FeaturedArtworksSectionProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [featuredIndex, setFeaturedIndex] = useState(0);

  const visibleCards = isMobile ? 1 : isTablet ? 2 : 3;
  const maxIndex = Math.max(0, artworks.length - visibleCards);
  const showCarousel = artworks.length > visibleCards;
  const slideWidth = 100 / visibleCards;

  const handlePrevFeatured = () => {
    setFeaturedIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNextFeatured = () => {
    setFeaturedIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  useEffect(() => {
    setFeaturedIndex(0);
  }, [artworks.length, visibleCards]);
  const { palette } = useSiteTheme();

  return (
    //"background.paper"
    <PageSection bg={palette.featured}>
      <Box sx={{ textAlign: "center", mb: 2.5 }}>
        <SectionHeader>New and Featured</SectionHeader>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          A selection of recent pieces and studio favourites.
        </Typography>
      </Box>

      {!showCarousel ? (
        <Grid container spacing={3} alignItems="stretch">
          {artworks.map((artwork) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={artwork.id}
              sx={{ display: "flex" }}
            >
              <ArtworkCard artwork={artwork} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ position: "relative" }}>
          <Box sx={{ overflow: "hidden", mx: -1.5 }}>
            <Box
              sx={{
                display: "flex",
                transition: "transform 0.45s ease",
                transform: `translateX(-${featuredIndex * slideWidth}%)`,
              }}
            >
              {artworks.map((artwork) => (
                <Box
                  key={artwork.id}
                  sx={{
                    flex: `0 0 ${slideWidth}%`,
                    px: 1.5,
                    boxSizing: "border-box",
                  }}
                >
                  <ArtworkCard artwork={artwork} />
                </Box>
              ))}
            </Box>
          </Box>

          <IconButton
            onClick={handlePrevFeatured}
            disabled={featuredIndex === 0}
            sx={{
              position: "absolute",
              left: { xs: 8, md: -20 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: palette.controlBg,
              border: `1px solid ${palette.controlBorder}`,
              boxShadow: 1,
              color: palette.controlText,
              "&:hover": {
                bgcolor: palette.controlHoverBg,
              },
              "&.Mui-disabled": {
                opacity: 0.4,
                color: palette.controlText,
              },
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            onClick={handleNextFeatured}
            disabled={featuredIndex === maxIndex}
            sx={{
              position: "absolute",
              right: { xs: 8, md: -20 },
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: palette.controlBg,
              border: `1px solid ${palette.controlBorder}`,
              boxShadow: 1,
              color: palette.controlText,
              "&:hover": {
                bgcolor: palette.controlHoverBg,
              },
              "&.Mui-disabled": {
                opacity: 0.4,
                color: palette.controlText,
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}
    </PageSection>
  );
}
